import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  accountService,
  type AccountImportData,
  type AccountImportResult,
} from "~/services/contabilidad/accountService";
import { ACCOUNTS_QUERY_KEY } from "./useAccounts";

interface ImportAccountsParams {
  companyId: string;
  accounts: AccountImportData[];
}

export function useAccountImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      accounts,
    }: ImportAccountsParams): Promise<AccountImportResult> => {
      return accountService.createBatch(companyId, accounts);
    },
    onSuccess: (result, { companyId }) => {
      // Invalidate all account queries
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...ACCOUNTS_QUERY_KEY, "company", companyId],
      });

      // Build result message
      const messages: string[] = [];
      if (result.created > 0) {
        messages.push(`${result.created} cuenta${result.created > 1 ? "s" : ""} creada${result.created > 1 ? "s" : ""}`);
      }
      if (result.skipped > 0) {
        messages.push(`${result.skipped} omitida${result.skipped > 1 ? "s" : ""} (ya existen)`);
      }
      if (result.errors.length > 0) {
        messages.push(`${result.errors.length} error${result.errors.length > 1 ? "es" : ""}`);
      }

      const message = messages.join(", ");

      if (result.errors.length > 0) {
        toast.warning("Importación completada con errores", {
          description: message,
        });
      } else if (result.created > 0) {
        toast.success("Importación exitosa", {
          description: message,
        });
      } else {
        toast.info("Sin cambios", {
          description: "Todas las cuentas ya existen en la empresa",
        });
      }
    },
    onError: (error: Error) => {
      toast.error(`Error al importar cuentas: ${error.message}`);
    },
  });
}
